import type { ClassDef, ClassToken } from "./types";

/**
 * Canonical WoW classes, IDs, and spec IDs. Class IDs come from
 * GetClassInfo / UnitClass; spec IDs come from
 * GetSpecializationInfoForClassID. These are stable across patches.
 *
 * Spec icons reference Wowhead's CDN names (well-known).
 */
export const CLASSES: ClassDef[] = [
  {
    id: 1, token: "warrior", name: "Warrior", iconName: "classicon_warrior",
    specs: [
      { id: 71, classToken: "warrior", name: "Arms",       role: "dps",  iconName: "ability_warrior_savageblow" },
      { id: 72, classToken: "warrior", name: "Fury",       role: "dps",  iconName: "ability_warrior_innerrage" },
      { id: 73, classToken: "warrior", name: "Protection", role: "tank", iconName: "ability_warrior_defensivestance" },
    ],
  },
  {
    id: 2, token: "paladin", name: "Paladin", iconName: "classicon_paladin",
    specs: [
      { id: 65, classToken: "paladin", name: "Holy",         role: "healer", iconName: "spell_holy_holybolt" },
      { id: 66, classToken: "paladin", name: "Protection",   role: "tank",   iconName: "ability_paladin_shieldofthetemplar" },
      { id: 70, classToken: "paladin", name: "Retribution",  role: "dps",    iconName: "spell_holy_auraoflight" },
    ],
  },
  {
    id: 3, token: "hunter", name: "Hunter", iconName: "classicon_hunter",
    specs: [
      { id: 253, classToken: "hunter", name: "Beast Mastery", role: "dps", iconName: "ability_hunter_bestialdiscipline" },
      { id: 254, classToken: "hunter", name: "Marksmanship",  role: "dps", iconName: "ability_hunter_focusedaim" },
      { id: 255, classToken: "hunter", name: "Survival",      role: "dps", iconName: "ability_hunter_camouflage" },
    ],
  },
  {
    id: 4, token: "rogue", name: "Rogue", iconName: "classicon_rogue",
    specs: [
      { id: 259, classToken: "rogue", name: "Assassination", role: "dps", iconName: "ability_rogue_eviscerate" },
      { id: 260, classToken: "rogue", name: "Outlaw",        role: "dps", iconName: "inv_sword_30" },
      { id: 261, classToken: "rogue", name: "Subtlety",      role: "dps", iconName: "ability_stealth" },
    ],
  },
  {
    id: 5, token: "priest", name: "Priest", iconName: "classicon_priest",
    specs: [
      { id: 256, classToken: "priest", name: "Discipline", role: "healer", iconName: "spell_holy_powerwordshield" },
      { id: 257, classToken: "priest", name: "Holy",       role: "healer", iconName: "spell_holy_guardianspirit" },
      { id: 258, classToken: "priest", name: "Shadow",     role: "dps",    iconName: "spell_shadow_shadowwordpain" },
    ],
  },
  {
    id: 6, token: "deathknight", name: "Death Knight", iconName: "classicon_deathknight",
    specs: [
      { id: 250, classToken: "deathknight", name: "Blood",  role: "tank", iconName: "spell_deathknight_bloodpresence" },
      { id: 251, classToken: "deathknight", name: "Frost",  role: "dps",  iconName: "spell_deathknight_frostpresence" },
      { id: 252, classToken: "deathknight", name: "Unholy", role: "dps",  iconName: "spell_deathknight_unholypresence" },
    ],
  },
  {
    id: 7, token: "shaman", name: "Shaman", iconName: "classicon_shaman",
    specs: [
      { id: 262, classToken: "shaman", name: "Elemental",   role: "dps",    iconName: "spell_nature_lightning" },
      { id: 263, classToken: "shaman", name: "Enhancement", role: "dps",    iconName: "spell_shaman_improvedstormstrike" },
      { id: 264, classToken: "shaman", name: "Restoration", role: "healer", iconName: "spell_nature_magicimmunity" },
    ],
  },
  {
    id: 8, token: "mage", name: "Mage", iconName: "classicon_mage",
    specs: [
      { id: 62, classToken: "mage", name: "Arcane", role: "dps", iconName: "spell_holy_magicalsentry" },
      { id: 63, classToken: "mage", name: "Fire",   role: "dps", iconName: "spell_fire_firebolt02" },
      { id: 64, classToken: "mage", name: "Frost",  role: "dps", iconName: "spell_frost_frostbolt02" },
    ],
  },
  {
    id: 9, token: "warlock", name: "Warlock", iconName: "classicon_warlock",
    specs: [
      { id: 265, classToken: "warlock", name: "Affliction",  role: "dps", iconName: "spell_shadow_deathcoil" },
      { id: 266, classToken: "warlock", name: "Demonology",  role: "dps", iconName: "spell_shadow_metamorphosis" },
      { id: 267, classToken: "warlock", name: "Destruction", role: "dps", iconName: "spell_shadow_rainoffire" },
    ],
  },
  {
    id: 10, token: "monk", name: "Monk", iconName: "classicon_monk",
    specs: [
      { id: 268, classToken: "monk", name: "Brewmaster", role: "tank",   iconName: "spell_monk_brewmaster_spec" },
      { id: 269, classToken: "monk", name: "Windwalker", role: "dps",    iconName: "spell_monk_windwalker_spec" },
      { id: 270, classToken: "monk", name: "Mistweaver", role: "healer", iconName: "spell_monk_mistweaver_spec" },
    ],
  },
  {
    id: 11, token: "druid", name: "Druid", iconName: "classicon_druid",
    specs: [
      { id: 102, classToken: "druid", name: "Balance",     role: "dps",    iconName: "spell_nature_starfall" },
      { id: 103, classToken: "druid", name: "Feral",       role: "dps",    iconName: "ability_druid_catform" },
      { id: 104, classToken: "druid", name: "Guardian",    role: "tank",   iconName: "ability_racial_bearform" },
      { id: 105, classToken: "druid", name: "Restoration", role: "healer", iconName: "spell_nature_healingtouch" },
    ],
  },
  {
    id: 12, token: "demonhunter", name: "Demon Hunter", iconName: "classicon_demonhunter",
    specs: [
      { id: 577, classToken: "demonhunter", name: "Havoc",     role: "dps",  iconName: "ability_demonhunter_specdps" },
      { id: 581, classToken: "demonhunter", name: "Vengeance", role: "tank", iconName: "ability_demonhunter_spectank" },
      { id: 1480, classToken: "demonhunter", name: "Devourer", role: "dps", iconName: "classicon_demonhunter_void" },
    ],
  },
  {
    id: 13, token: "evoker", name: "Evoker", iconName: "classicon_evoker",
    specs: [
      { id: 1467, classToken: "evoker", name: "Devastation",  role: "dps",    iconName: "classicon_evoker_devastation" },
      { id: 1468, classToken: "evoker", name: "Preservation", role: "healer", iconName: "classicon_evoker_preservation" },
      { id: 1473, classToken: "evoker", name: "Augmentation", role: "dps",    iconName: "classicon_evoker_augmentation" },
    ],
  },
];

export const CLASS_BY_ID: Record<number, ClassDef> = Object.fromEntries(
  CLASSES.map((c) => [c.id, c]),
);
export const CLASS_BY_TOKEN: Record<ClassToken, ClassDef> = Object.fromEntries(
  CLASSES.map((c) => [c.token, c]),
) as Record<ClassToken, ClassDef>;

/** Flat lookup of every spec across every class. */
export const SPEC_BY_ID: Record<number, { spec: ClassDef["specs"][number]; classDef: ClassDef }> =
  Object.fromEntries(
    CLASSES.flatMap((c) => c.specs.map((s) => [s.id, { spec: s, classDef: c }])),
  );

export function getSpecsForClass(classID: number) {
  return CLASS_BY_ID[classID]?.specs ?? [];
}

/** Returns true if the item's eligibleSpecs contains AT LEAST ONE spec
 *  belonging to the given class. Used to filter pool views so a Druid
 *  doesn't see cloth items (only Priest/Mage/Warlock specs eligible),
 *  even though the engine math already filters them out — there's no
 *  point cluttering the UI with items the user can never win. */
export function isItemEligibleForClass(
  eligibleSpecs: number[] | undefined,
  classID: number,
): boolean {
  if (!eligibleSpecs || eligibleSpecs.length === 0) return false;
  const cls = CLASS_BY_ID[classID];
  if (!cls) return false;
  for (const spec of cls.specs) {
    if (eligibleSpecs.includes(spec.id)) return true;
  }
  return false;
}
